import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import { requestCustomAuth } from '@/services/docs-site-auth/requestCustomAuth';
import type { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import { ErrorCode } from '@/shared/constant/error/ErrorCode';
import type { DocsSiteVisibilitySettings } from '@tarslib/types/lib/publishedProject';
import clsx from 'clsx';
import React from 'react';

export type CustomAuthProps = {
  className?: string;
  docsSiteId: number;
  projectId: number;
  subdirectory?: string;
  visitType?: DocsSiteVisitType;
  visibilitySettings: DocsSiteVisibilitySettings;
};

export function CustomAuth(props: CustomAuthProps) {
  const { className, docsSiteId, projectId, subdirectory, visitType, visibilitySettings } = props;
  const [errorMessage, setErrorMessage] = React.useState('');
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const envConfig = useEnvConfigModelStore((x) => x);

  React.useEffect(() => {
    (async () => {
      try {
        const authToken = new URL(window.location.href).searchParams.get('auth_token');
        if (authToken) {
          const [error] = await requestCustomAuth(
            {
              id: docsSiteId,
              projectId,
              authToken,
            },
            envConfig,
            clientI18n,
            visitType,
            subdirectory,
          );
          if (!error) {
            const url = new URL(window.location.href);
            url.searchParams.delete('auth_token');
            const newUrl = url.toString();
            window.location.replace(newUrl);
          } else {
            const errorObj = error.getErrorCodeMessageType();
            if (errorObj.errorCode === ErrorCode.Unauthorized) {
              setErrorMessage(clientI18n('docsSite.login.error.detail'));
            } else {
              setErrorMessage(JSON.stringify(errorObj));
            }
          }
        } else {
          const url = new URL(visibilitySettings.customAuth?.loginUrl ?? '');
          url.searchParams.delete('auth_token');
          url.searchParams.set('redirect', window.location.href);
          const newUrl = url.toString();
          window.location.replace(newUrl);
        }
      } catch (e: any) {
        console.error(e);
        setErrorMessage(String(e));
      }
    })();
  }, [docsSiteId, projectId, subdirectory, visitType, visibilitySettings, clientI18n, envConfig]);

  return (
    <div
      className={clsx(
        'flex w-[560px] flex-col items-start justify-center gap-8 rounded-3xl p-10',
        className,
      )}
      style={{
        boxShadow: '0px 0px 40px 0px rgba(0, 0, 0, 0.02), 0px 0px 104px 0px rgba(0, 0, 0, 0.06)',
      }}
    >
      {errorMessage && (
        <div className="text-h2 font-600 text-color">{clientI18n('docsSite.login.error')}</div>
      )}

      <div className="text-lg font-400 text-secondary">
        {errorMessage || clientI18n('api.customAuth.redirect.title')}
      </div>
    </div>
  );
}
